import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Row = { host: string; source: string; input: string };

function normalize(results: any[]): Row[] {
  if (!Array.isArray(results)) return [];
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const it of results) {
    const host = String(it?.host ?? '');
    if (!host) continue;
    const input = String(it?.input ?? '');
    const source = String(it?.source ?? '');
    const key = `${host}::${source}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ host, source, input });
  }
  return out;
}

const PAGE_SIZE = 15;

const SubdomainsResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const rows = normalize(results);
  const [page, setPage] = React.useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const slice = rows.slice(start, start + PAGE_SIZE);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.subdomains.empty')}</p>;
  }

  return (
    <div className="w-full">
      {/* Cabecera con contador */}
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">
          {t('tools.results_scan.subdomains.title', { count: rows.length })}
        </h4>
        <div className="text-xs text-slate-500">
          {t('tools.results_scan.pagination.page_of', { page, total: totalPages })}
        </div>
      </div>

      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gradient-to-r from-sky-50 to-indigo-50">
            <tr className="text-slate-700">
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.subdomains.columns.host')}
              </th>
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.subdomains.columns.source')}
              </th>
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.subdomains.columns.input')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {slice.map((r, i) => (
              <tr
                key={`${r.host}-${r.source}-${i}`}
                className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}
              >
                <td className="px-3 py-2 font-medium text-slate-900 break-all">{r.host}</td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-flex items-center rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 bg-white">
                    {r.source || '-'}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-slate-600">{r.input || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-center gap-3 text-sm">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className={`px-3 py-1.5 rounded-lg border ${
              page === 1
                ? 'text-slate-400 border-slate-200 cursor-not-allowed'
                : 'text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {t('tools.results_scan.pagination.prev')}
          </button>
          <span className="text-slate-500">
            {t('tools.results_scan.pagination.page_of_short', { page, total: totalPages })}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className={`px-3 py-1.5 rounded-lg border ${
              page === totalPages
                ? 'text-slate-400 border-slate-200 cursor-not-allowed'
                : 'text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {t('tools.results_scan.pagination.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubdomainsResults;
